import { LinkedinLogo } from "@phosphor-icons/react";

import {
  MobileSelectionList,
  MobileSelectionListItem,
} from "common/components/MobileSelectionList";
import ProfileImage from "common/components/ui/ProfileImage";
import Skeleton from "common/components/ui/Skeleton";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";

import { NEW_CAMPAIGN_AUDIENCE_MOBILE_TAGS } from "../../../../../constants";

import type { Dispatch, ReactNode, SetStateAction } from "react";
import type { AudienceProfile } from "../../../../../types";

interface AudienceMobileListProps {
  rows: AudienceProfile[];
  isLoading: boolean;
  pageSize: number;
  totalItemCount: number;
  selectedProfiles: AudienceProfile[];
  setSelectedProfiles: Dispatch<SetStateAction<AudienceProfile[]>>;
  emptyStateComponent: ReactNode;
}

export default function AudienceMobileList({
  rows,
  isLoading,
  pageSize,
  totalItemCount,
  selectedProfiles,
  setSelectedProfiles,
  emptyStateComponent,
}: AudienceMobileListProps) {
  const { permissions } = useSelectedWorkspaceContext();

  if (rows && !rows.length && !isLoading) {
    return emptyStateComponent;
  }

  return (
    <MobileSelectionList
      title="Targets"
      data={rows || new Array(pageSize).fill(null)}
      selection={selectedProfiles}
      onSelectionChange={
        isLoading
          ? undefined
          : (newSelection) => {
              setSelectedProfiles(newSelection === "all" ? rows : newSelection);
            }
      }
      allItems={NEW_CAMPAIGN_AUDIENCE_MOBILE_TAGS}
      totalDataCount={totalItemCount}
      canSelectAll={false}
    >
      {(row, listItemProps) => {
        if (isLoading) {
          return (
            <MobileSelectionListItem {...listItemProps}>
              <Skeleton className="size-8 shrink-0 rounded-full" />
              <Skeleton className="w-28 h-4" />
            </MobileSelectionListItem>
          );
        }
        const { id, picture_url, full_name, premium } = row;

        return (
          <MobileSelectionListItem
            {...listItemProps}
            relativeTo={
              permissions.includes("leads_full") ? `leads/${id}` : undefined
            }
          >
            <ProfileImage size="sm" src={picture_url} />

            <div className="flex min-w-0 flex-1 flex-col gap-x-1 text-left">
              <div className="flex items-center gap-x-1">
                <span className="truncate text-sm-bold">{full_name}</span>
                {premium && (
                  <LinkedinLogo
                    className="size-4 shrink-0 text-yellow-500"
                    weight="fill"
                  />
                )}
              </div>
            </div>
          </MobileSelectionListItem>
        );
      }}
    </MobileSelectionList>
  );
}
